import {
  Button,
  XStack,
  YStack,
  SizableText,
  Stack,
  Error,
  Paragraph,
  ScrollView,
  Separator,
} from '@my/ui'
import { GroupCreationFormData } from './use-group-creation-wizard'
import { SolitoImage } from 'solito/image'
import { Lock } from '@tamagui/lucide-icons'
import { GroupCreationHeader } from './group-creation-header'
import { GroupAvatar } from 'app/features/group/components'

export interface GroupCreationPublishProps {
  loading?: boolean
  data: GroupCreationFormData
  error?: string
  onEdit: () => void
  onSubmit: () => void
  onBack: () => void
}

export const GroupCreationPublish = (props: GroupCreationPublishProps) => {
  const coverImage = props.data.coverImage?.uri
  const profileImage = props.data.profileImage?.uri

  const categories = [props.data.mainCategory, ...props.data.subCategories].map(
    (category) => category.label
  )
  const uniqueCategories = [...new Set(categories)]

  return (
    <YStack flex={1} {...props}>
      <GroupCreationHeader title="Group overview" onBack={props.onBack}>
        <Button onPress={props.onEdit} size="$3" theme="white" outlined>
          Edit
        </Button>
        <Button onPress={props.onSubmit} loading={props.loading} size="$3" theme="primary">
          Publish
        </Button>
      </GroupCreationHeader>

      <Separator bg="$white" pt="$3.5" boc="$overlay8" w="100%" />

      <ScrollView flex={1} contentContainerStyle={{ gap: 32, paddingTop: 16 }}>
        {coverImage && (
          <XStack aspectRatio={2 / 1} overflow="hidden" br="$3">
            <SolitoImage src={coverImage} fill contentFit="cover" alt="Cover Image" />
          </XStack>
        )}

        <XStack gap="$3.5" ai="center">
          <GroupAvatar src={profileImage} width={96} height={96} />

          <SizableText flex={1} size="$8" fow="600" numberOfLines={3}>
            {props.data.name}
          </SizableText>
        </XStack>

        <YStack gap="$1">
          <SizableText size="$4" fow="500">
            Description
          </SizableText>
          <Paragraph color="$grey8" size="$3" $gtMd={{ size: '$4' }} whiteSpace="pre-wrap">
            {props.data.description}
          </Paragraph>
        </YStack>

        <YStack gap="$2">
          <SizableText size="$4" fow="500">
            Categories
          </SizableText>
          <XStack gap="$2" flexWrap="wrap">
            {uniqueCategories.map((category) => (
              <Stack px="$3" py="$2" bg="$grey3" br="$2" key={category}>
                <SizableText color="$grey7">{category}</SizableText>
              </Stack>
            ))}
          </XStack>
        </YStack>

        <YStack gap="$1">
          <XStack ai="center" gap="$2">
            <Lock size={16} />
            <SizableText size="$4" fow="500">
              Group Privacy
            </SizableText>
          </XStack>
          <SizableText color="$grey8" size="$3" $gtMd={{ size: '$4' }}>
            {props.data.visibility === 'public' ? 'Public' : 'Private'}
          </SizableText>
        </YStack>

        {props.error && <Error mt="auto" error={props.error} />}
      </ScrollView>
    </YStack>
  )
}
